/**
 * CanvasBackgroundSelector — top-nav popover.
 * Lets the user toggle the dynamic background and pick:
 *   • a built-in suggestion (public/img/fondos/*),
 *   • a video/image asset from their gallery.
 * Tweaks opacity & blur on the fly.
 */
import { useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Wallpaper, X, Loader2, Check, Music as _Music, Video as VideoIcon } from 'lucide-react';
import axios from 'axios';
import useCanvasBgStore from '../../store/canvasBgStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTED = [
  { url: '/img/fondos/fondo_1.png', kind: 'image', label: 'Nebula' },
  { url: '/img/fondos/fondo_2.jpg', kind: 'image', label: 'Golden Hour' },
];

/**
 * placement:
 *   'nav'    (por defecto) botón de la barra superior, popover que baja.
 *   'corner' Envío LMX-8a · vista nueva: botón compacto en la esquina inferior
 *            izquierda del canvas, a la derecha de «encuadrar», con la estética
 *            de los controles de zoom; el popover se despliega hacia arriba.
 *   'rail'   I-48 · botón cuadrado de sólo ícono en el panel de funciones del
 *            canvas (encima del zoom). La etiqueta se desliza al pasar el
 *            mouse y el popover se abre hacia la derecha.
 */
const CanvasBackgroundSelector = ({ placement = 'nav' }) => {
  const corner = placement === 'corner';
  const rail = placement === 'rail';
  const { enabled, url, kind, label, opacity, blur, setEnabled, setBackground, clearBackground, setOpacity, setBlur } =
    useCanvasBgStore();

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('suggested'); // 'suggested' | 'gallery'
  const [galleryAssets, setGalleryAssets] = useState(null);
  const [loadingGallery, setLoadingGallery] = useState(false);
  const popRef = useRef(null);
  const btnRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (popRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Lazy-load gallery when switching to that tab
  useEffect(() => {
    if (!open || tab !== 'gallery' || galleryAssets) return;
    setLoadingGallery(true);
    axios
      .get(`${API}/gallery?limit=60`)
      .then((res) => setGalleryAssets((res.data.assets || []).filter((a) => a.asset_type !== 'audio')))
      .catch(() => setGalleryAssets([]))
      .finally(() => setLoadingGallery(false));
  }, [open, tab, galleryAssets]);

  const isSelected = (testUrl) => url === testUrl;

  const pickAsset = (asset) => {
    setBackground({
      url: asset.url,
      kind: asset.asset_type === 'video' ? 'video' : 'image',
      label: asset.filename || asset.label || 'Asset',
    });
  };

  return (
    <>
      {rail ? (
        <button
          ref={btnRef}
          type="button"
          className={`canvas-tool-btn ${enabled && url ? 'active' : ''} ${open ? 'is-open' : ''}`}
          onClick={() => setOpen((o) => !o)}
          aria-label="Fondo dinámico"
          aria-expanded={open}
          data-testid="canvas-bg-toggle"
        >
          <Wallpaper size={14} />
          <span className="canvas-tool-label">Fondo dinámico</span>
          {enabled && url && <span className="canvas-tool-dot" aria-label="Fondo activo" />}
        </button>
      ) : (
      <button
        ref={btnRef}
        className={corner
          ? `canvas-bg-corner-btn ${enabled && url ? 'active' : ''}`
          : `nav-projects-btn canvas-bg-trigger ${enabled && url ? 'active' : ''}`}
        onClick={() => setOpen((o) => !o)}
        title="Fondo dinámico del canvas"
        data-testid="canvas-bg-toggle"
      >
        <ImageIcon size={corner ? 13 : 15} />
        Fondo
        {enabled && url && <span className="canvas-bg-dot" />}
      </button>
      )}

      {open && (
        <div
          ref={popRef}
          className={`canvas-bg-popover ${corner ? 'is-up nowheel nopan nodrag' : ''} ${rail ? 'is-side nowheel nopan nodrag' : ''}`}
          data-testid="canvas-bg-popover"
        >
          <div className="canvas-bg-pop-header">
            <span className="canvas-bg-pop-title">Fondo dinámico</span>
            <button className="canvas-bg-pop-close" onClick={() => setOpen(false)} data-testid="canvas-bg-close">
              <X size={14} />
            </button>
          </div>

          {/* Toggle */}
          <label className="canvas-bg-switch-row">
            <span>Activar fondo dinámico</span>
            <input
              type="checkbox"
              checked={!!enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              data-testid="canvas-bg-switch"
              className="canvas-bg-switch"
            />
          </label>

          {/* Tabs */}
          <div className="canvas-bg-tabs">
            <button
              className={`canvas-bg-tab ${tab === 'suggested' ? 'active' : ''}`}
              onClick={() => setTab('suggested')}
              data-testid="canvas-bg-tab-suggested"
            >
              Sugeridos
            </button>
            <button
              className={`canvas-bg-tab ${tab === 'gallery' ? 'active' : ''}`}
              onClick={() => setTab('gallery')}
              data-testid="canvas-bg-tab-gallery"
            >
              Mi Galería
            </button>
          </div>

          {/* Grid */}
          <div className="canvas-bg-grid" data-testid="canvas-bg-grid">
            {tab === 'suggested' && SUGGESTED.map((s) => (
              <button
                key={s.url}
                className={`canvas-bg-card ${isSelected(s.url) ? 'selected' : ''}`}
                onClick={() => setBackground(s)}
                data-testid={`canvas-bg-suggested-${s.label}`}
                title={s.label}
              >
                <img src={s.url} alt={s.label} />
                <span className="canvas-bg-card-label">{s.label}</span>
                {isSelected(s.url) && <span className="canvas-bg-card-check"><Check size={12} /></span>}
              </button>
            ))}

            {tab === 'gallery' && (
              <>
                {loadingGallery && (
                  <div className="canvas-bg-empty"><Loader2 size={18} className="spin" /></div>
                )}
                {!loadingGallery && galleryAssets?.length === 0 && (
                  <div className="canvas-bg-empty">No hay imágenes ni videos en tu galería.</div>
                )}
                {!loadingGallery && galleryAssets?.map((a) => (
                  <button
                    key={a.id}
                    className={`canvas-bg-card ${isSelected(a.url) ? 'selected' : ''}`}
                    onClick={() => pickAsset(a)}
                    data-testid={`canvas-bg-gallery-${a.id}`}
                    title={a.filename}
                  >
                    {a.asset_type === 'video' ? (
                      <>
                        <video src={a.url} muted preload="none" />
                        <span className="canvas-bg-card-badge"><VideoIcon size={10} /> Video</span>
                      </>
                    ) : (
                      <img src={a.url} alt={a.filename} />
                    )}
                    <span className="canvas-bg-card-label">{a.filename}</span>
                    {isSelected(a.url) && <span className="canvas-bg-card-check"><Check size={12} /></span>}
                  </button>
                ))}
              </>
            )}
          </div>

          {/* Sliders */}
          <div className="canvas-bg-sliders">
            <label className="canvas-bg-slider">
              <span>Opacidad</span>
              <input
                type="range" min="0" max="1" step="0.05"
                value={opacity}
                onChange={(e) => setOpacity(parseFloat(e.target.value))}
                data-testid="canvas-bg-opacity"
              />
              <em>{Math.round(opacity * 100)}%</em>
            </label>
            <label className="canvas-bg-slider">
              <span>Desenfoque</span>
              <input
                type="range" min="0" max="20" step="1"
                value={blur}
                onChange={(e) => setBlur(parseInt(e.target.value, 10))}
                data-testid="canvas-bg-blur"
              />
              <em>{blur}px</em>
            </label>
          </div>

          {url && (
            <div className="canvas-bg-current">
              <span className="canvas-bg-current-label">
                Actual: <strong>{label || (kind === 'video' ? 'Video' : 'Imagen')}</strong>
              </span>
              <button
                className="canvas-bg-clear-btn"
                onClick={clearBackground}
                data-testid="canvas-bg-clear"
              >
                Quitar
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default CanvasBackgroundSelector;
